/**
 * EmailChangeModal Component
 *
 * USER-ONLY modal for email address change functionality.
 * Located in UserAccountManagement folder to signal this is NOT shared with admin users.
 * Admins should NOT be able to self-change their email for security reasons.
 * Extracted from UserAccountManagement for better code organization.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';

// 2. Internal absolute imports
import {
  Button,
  Input,
  Form,
  Alert,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@enfomentis/ui';
import { useAuth } from '@/hooks/useAuth';
import { useToastNotification } from '@enfomentis/ui';
import type { EmailChangeRequest, AuthErrorCode } from '@/types/auth';

/**
 * Props for EmailChangeModal
 */
export interface EmailChangeModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  onSuccess?: () => void;
  onEmailChange?: () => void;
}

/**
 * Email Change Modal Component (USER-ONLY)
 *
 * Features:
 * - New email validation
 * - Current password requirement for security
 * - Duplicate email detection
 * - Verification email sent to new address
 * - Success toast notification
 * - Important warnings about verification process
 */
export const EmailChangeModal: React.FC<EmailChangeModalProps> = ({
  isOpen,
  onOpenChange,
  onSuccess,
  onEmailChange
}) => {
  const { user, requestEmailChange } = useAuth();
  const toast = useToastNotification();

  // Form state
  const [emailFormData, setEmailFormData] = useState<EmailChangeRequest>({
    newEmail: '',
    currentPassword: ''
  });
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailServerErrors, setEmailServerErrors] = useState<Record<string, string>>({});
  const [hasEmailSubmitted, setHasEmailSubmitted] = useState(false);
  const [isEmailCurrentPasswordVisible, setIsEmailCurrentPasswordVisible] = useState(false);
  const [isEmailSuccess, setIsEmailSuccess] = useState(false);

  // Toggle password visibility
  const toggleEmailCurrentPasswordVisibility = useCallback(() => {
    setIsEmailCurrentPasswordVisible(prev => !prev);
  }, []);

  // Email validation function
  const validateNewEmail = (value: string): string | null => {
    if (!value) return "New email address is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    if (value === user?.email) return "New email must be different from current email";
    return null;
  };

  // Password validation function
  const validateEmailCurrentPassword = (value: string): string | null => {
    if (!value) return "Current password is required to change email";
    return null;
  };

  // Email validation state
  const newEmailError = hasEmailSubmitted ? validateNewEmail(emailFormData.newEmail) : null;
  const emailCurrentPasswordError = hasEmailSubmitted ? validateEmailCurrentPassword(emailFormData.currentPassword) : null;

  // Reset email form state
  const resetEmailForm = useCallback(() => {
    setEmailFormData({
      newEmail: '',
      currentPassword: ''
    });
    setEmailServerErrors({});
    setHasEmailSubmitted(false);
    setIsEmailSubmitting(false);
    setIsEmailSuccess(false);
    setIsEmailCurrentPasswordVisible(false);
  }, []);

  // Handle email modal close
  const handleEmailModalClose = useCallback(() => {
    resetEmailForm();
    onOpenChange();
  }, [resetEmailForm, onOpenChange]);

  // Map email change errors to form fields
  const mapEmailErrorToField = (error: AuthErrorCode): string | null => {
    switch (error) {
      case 'INVALID_EMAIL_FORMAT':
      case 'EMAIL_ALREADY_IN_USE':
      case 'VALIDATION_ERROR':
        return 'newEmail';
      case 'INVALID_CREDENTIALS':
        return 'currentPassword';
      default:
        return null;
    }
  };

  // Handle email change form submission
  const handleEmailChange = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasEmailSubmitted(true);

    // Client-side validation before submission
    const newEmailValidationError = validateNewEmail(emailFormData.newEmail);
    const currentPasswordValidationError = validateEmailCurrentPassword(emailFormData.currentPassword);

    if (newEmailValidationError || currentPasswordValidationError) {
      return; // Don't submit if there are validation errors
    }

    setIsEmailSubmitting(true);
    setEmailServerErrors({});

    try {
      const result = await requestEmailChange(emailFormData);

      if (result.success) {
        setIsEmailSuccess(true);

        // Show success toast notification
        toast.showToast({
          title: 'Verification email sent',
          description: result.message,
          color: 'success',
          timeout: 4000
        });

        // Call callbacks if provided
        if (onEmailChange) {
          onEmailChange();
        }
        if (onSuccess) {
          onSuccess();
        }

        // Auto-close modal after 2 seconds on success
        setTimeout(() => {
          handleEmailModalClose();
        }, 2000);

      } else {
        // Handle error response
        if (result.error) {
          const errorField = mapEmailErrorToField(result.error);
          if (errorField) {
            setEmailServerErrors({ [errorField]: result.message });
          } else {
            setEmailServerErrors({ general: result.message });
          }
        } else {
          setEmailServerErrors({ general: result.message });
        }
      }
    } catch (error) {
      console.error('Email change error:', error);
      setEmailServerErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="lg"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      isDismissable={!isEmailSubmitting}
      isKeyboardDismissDisabled={isEmailSubmitting}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">Change Email Address</h3>
              <p className="text-sm text-text-secondary font-normal">
                Update your account email address. A verification email will be sent to your new address.
              </p>
            </ModalHeader>

            <ModalBody>
              {isEmailSuccess ? (
                // Success state
                <div className="text-center py-8">
                  <div className="text-success-600 text-6xl mb-4">✓</div>
                  <h4 className="text-lg font-medium text-text-primary mb-2">
                    Verification Email Sent
                  </h4>
                  <p className="text-text-secondary">
                    Check your new email address for a verification link. This modal will close automatically.
                  </p>
                </div>
              ) : (
                // Form state
                <Form
                  validationBehavior="aria"
                  validationErrors={emailServerErrors}
                  onSubmit={handleEmailChange}
                  className="space-y-6"
                >
                  {/* General error alert */}
                  {emailServerErrors['general'] && (
                    <Alert color="danger" variant="flat">
                      {emailServerErrors['general']}
                    </Alert>
                  )}

                  {/* New Email Input */}
                  <Input
                    type="email"
                    label="New Email Address"
                    placeholder="Enter your new email address"
                    value={emailFormData.newEmail}
                    onValueChange={(value) => setEmailFormData(prev => ({ ...prev, newEmail: value }))}
                    validate={validateNewEmail}
                    isRequired
                    autoComplete="email"
                    errorMessage={emailServerErrors['newEmail'] || newEmailError}
                    isInvalid={!!emailServerErrors['newEmail'] || !!newEmailError}
                    classNames={{
                      input: "text-base", // 16px font size to prevent mobile zoom
                    }}
                  />

                  {/* Current Password Input */}
                  <Input
                    type={isEmailCurrentPasswordVisible ? "text" : "password"}
                    label="Current Password"
                    placeholder="Enter your current password to confirm"
                    value={emailFormData.currentPassword}
                    onValueChange={(value) => setEmailFormData(prev => ({ ...prev, currentPassword: value }))}
                    validate={validateEmailCurrentPassword}
                    isRequired
                    autoComplete="current-password"
                    errorMessage={emailServerErrors['currentPassword'] || emailCurrentPasswordError}
                    isInvalid={!!emailServerErrors['currentPassword'] || !!emailCurrentPasswordError}
                    endContent={
                      <button
                        className="focus:outline-none"
                        type="button"
                        onClick={toggleEmailCurrentPasswordVisibility}
                        aria-label={isEmailCurrentPasswordVisible ? "Hide password" : "Show password"}
                      >
                        {isEmailCurrentPasswordVisible ? (
                          <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                        )}
                      </button>
                    }
                    classNames={{
                      input: "text-base", // 16px font size to prevent mobile zoom
                    }}
                  />

                  <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-warning-800 mb-2">Important:</h5>
                    <ul className="text-sm text-warning-700 space-y-1">
                      <li>• Your current email will remain active until you verify the new address</li>
                      <li>• Check your spam folder if you don't receive the verification email</li>
                      <li>• The verification link expires in 1 hour</li>
                    </ul>
                  </div>
                </Form>
              )}
            </ModalBody>

            {!isEmailSuccess && (
              <ModalFooter>
                <Button
                  color="default"
                  variant="light"
                  onPress={handleEmailModalClose}
                  isDisabled={isEmailSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  isLoading={isEmailSubmitting}
                  onPress={() => {
                    const form = document.querySelector('form') as HTMLFormElement;
                    if (form) {
                      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                    }
                  }}
                >
                  {isEmailSubmitting ? 'Sending...' : 'Send Verification Email'}
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
